import { JSX, useRef } from 'react'

import { Controller, FieldValues } from 'react-hook-form'

import {
  type CLSize,
  Group,
  type GroupProps,
  List,
} from '~/components/component-library'
import { ListItemProps } from '~/components/component-library/List/types'
import { RadioGroupInputProps } from '~/components/component-library/RadioGroupInput/types'
import { cn } from '~/utils/tailwind-utils'

const focusNextInput = (currentElement: HTMLElement) => {
  const focusable = Array.from(
    document.querySelectorAll<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => !el.closest('[disabled]') && el.offsetParent !== null)

  const currentIndex = focusable.indexOf(currentElement)
  if (currentIndex !== -1 && currentIndex + 1 < focusable.length) {
    focusable[currentIndex + 1].focus()
  }
}

const RadioCircle = ({ isSelected }: { isSelected: boolean }) => (
  <div
    className={cn(
      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-100',
      {
        'border-blue-500 bg-blue-500': isSelected,
        'border-gray-300 group-hover/item:border-gray-400 dark:border-gray-600 dark:group-hover/item:border-gray-500':
          !isSelected,
      },
    )}
  >
    <div
      className={cn(
        'h-2 w-2 rounded-full bg-white transition-transform duration-100',
        { 'scale-100': isSelected, 'scale-0': !isSelected },
      )}
    />
  </div>
)

const RadioGroupContent = ({
  options,
  currentValue,
  onSelect,
  label,
  description,
  footer,
  tooltip,
  secondaryDisplay,
  size = 'base',
  cta,
}: Pick<
  GroupProps,
  'label' | 'description' | 'footer' | 'tooltip' | 'secondaryDisplay'
> & {
  options: RadioGroupInputProps['options']
  currentValue: string
  onSelect: (value: string) => void
  size?: CLSize
  cta?: RadioGroupInputProps['cta']
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const currentIndex = options.findIndex((o) => o.value === currentValue)
      const nextIndex =
        e.key === 'ArrowDown'
          ? (currentIndex + 1) % options.length
          : (currentIndex - 1 + options.length) % options.length
      onSelect(options[nextIndex].value)
    } else if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault()
      if (containerRef.current) focusNextInput(containerRef.current)
    }
  }

  if (size === 'small') {
    return (
      <Group
        label={label}
        footer={footer}
        description={description}
        tooltip={tooltip}
      >
        <div
          ref={containerRef}
          role="radiogroup"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className="group/radiogroup -mx-2 -mt-1 flex w-[calc(100%+1rem)] flex-col rounded-lg py-0.5 outline-none ring-inset focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <div tabIndex={-1} className="flex flex-col outline-none">
            {options.map((option, index) => {
              const isSelected = option.value === currentValue
              return (
                <div
                  key={`radio-option-${index}`}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onSelect(option.value)}
                  className={cn(
                    'group/item relative flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
                    {
                      'group-focus-visible/radiogroup:after:pointer-events-none group-focus-visible/radiogroup:after:absolute group-focus-visible/radiogroup:after:inset-x-1.5 group-focus-visible/radiogroup:after:inset-y-1 group-focus-visible/radiogroup:after:rounded group-focus-visible/radiogroup:after:ring-2 group-focus-visible/radiogroup:after:ring-blue-500':
                        isSelected,
                    },
                  )}
                >
                  <RadioCircle isSelected={isSelected} />
                  <div className="text-sm text-primary">
                    {option.display ? option.display(isSelected) : option.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Group>
    )
  }

  const listItems: ListItemProps[] = options.map((option, index) => {
    const isSelected = option.value === currentValue
    return {
      key: `radio-option-${index}`,
      image: <RadioCircle isSelected={isSelected} />,
      content: option.display
        ? option.display(isSelected)
        : (option.label ?? ''),
      description: option.description,
      meta: option.meta,
      onClick: () => onSelect(option.value),
    }
  })

  return (
    <div role="radiogroup" className="w-full">
      <List
        items={listItems}
        cta={cta}
        label={label}
        footer={footer}
        description={description}
        tooltip={tooltip}
        secondaryDisplay={secondaryDisplay}
      />
    </div>
  )
}

export const RadioGroupInput = <FormType extends FieldValues = FieldValues>({
  control,
  inputName,
  options,
  label,
  description,
  footer,
  tooltip,
  secondaryDisplay,
  size = 'base',
  cta,
  ...rest
}: RadioGroupInputProps<FormType>): JSX.Element => {
  // Controlled mode (no react-hook-form)
  if (!control || !inputName) {
    const { value, onChange } = rest as {
      value: string
      onChange: (v: string) => void
    }
    return (
      <RadioGroupContent
        options={options}
        currentValue={value}
        onSelect={onChange}
        label={label}
        description={description}
        footer={footer}
        tooltip={tooltip}
        secondaryDisplay={secondaryDisplay}
        size={size}
        cta={cta}
      />
    )
  }

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field }) => (
        <RadioGroupContent
          options={options}
          currentValue={field.value}
          onSelect={field.onChange}
          label={label}
          description={description}
          footer={footer}
          tooltip={tooltip}
          secondaryDisplay={secondaryDisplay}
          size={size}
          cta={cta}
        />
      )}
    />
  )
}

RadioGroupInput.displayName = 'RadioGroupInput'

export type { RadioGroupInputProps } from '~/components/component-library/RadioGroupInput/types'
